import React, { useState, useEffect } from 'react';
import Moment from 'moment';
import { styled } from '@uhg-abyss/web/tools/styled';
import { V2Card } from '@uhg-abyss/web/ui/Card';
import { V2Tabs } from '@uhg-abyss/web/ui/Tabs';
import { V2Alert } from '@uhg-abyss/web/ui/Alert';
import { storage } from '@uhg-abyss/web/tools/storage';
import { useUser } from '@src/state/user/hooks';
import { useTinRegistration } from '@src/state/tinRegistration';
import { useTinRegistrationStatus } from '@src/state/tinRegistrationStatus';
import { config } from '@uhg-abyss/web/tools/config';
import { Loading } from '@src/common/Loading';
import { V2Text } from '@uhg-abyss/web/ui/Text';
import { SavedRegistrations } from './SavedRegistrations';
import { SubmittedRegistrations } from './SubmittedRegistrations';

const Wrapper = styled('div', {
  marginTop: '30px',
  width: '100%',
});

const Counter = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '$core.color.neutral.20',
  color: '$core.color.neutral.90',
  fontSize: '12px',
  fontWeight: 600,
  borderRadius: '4px',
  minWidth: '22px',
  height: '22px',
  padding: '0 6px',
  marginLeft: '8px',
  verticalAlign: 'middle',
});

const TabsWrapper = styled('div', {
  marginTop: '10px',
  // Lock the inner tablist to full width so tabs don't shift
  '& [role="tablist"]': {
    width: '100%',
  },
  // Each tab takes exactly half, locked — no grow/shrink on bold font change
  '& [role="tab"]': {
    flex: '0 0 50%',
    whiteSpace: 'normal',
    boxSizing: 'border-box',
    textAlign: 'left',
  },
  // Left-align the inner label layout (TabWrapperLayout > TabInnerLayout)
  '& [role="tab"] > div > div': {
    alignItems: 'flex-start',
  },
});

const SectionTitle = styled('p', {
  display: 'block',
  fontSize: '28px',
  fontWeight: 700,
  color: '$primary1',
});

const SectionText = styled(V2Text, {
  fontSize: '15px',
  lineHeight: '120%',
  fontWeight: 500,
});

export const ViewSubmissions = ({ onYes }) => {
  const [tinDraft, setTinDraft] = useState([]);
  const [submittedList, setSubmittedList] = useState([]);
  const [draftError, setDraftError] = useState(false);
  const [submittedError, setSubmittedError] = useState(false);

  const { uuid } = useUser.state();
  const isSuperUser = storage.local.get(uuid);
  const { retrieveDraft } = useTinRegistration.actions();
  const { retrieveDraftSaga } = useTinRegistration.state();
  const { searchRegistrationStatus } = useTinRegistrationStatus.actions();
  const { searchRegistrationStatusSaga } = useTinRegistrationStatus.state();
  const isSuperUserFeature = config('US3671382_SUPER_USER') && !!isSuperUser;

  const formatstartDate = (createdDate) => {
    const year = createdDate.slice(0, 4);
    const month = createdDate.slice(5, 7);
    const day = createdDate.slice(8, 10);
    return `${month}/${day}/${year}`;
  };

  const getExpirationDate = (createdDate) => {
    const res = new Date(createdDate);
    res.setDate(res.getDate() + 10);
    const date = res.getDate();
    const month = res.getMonth() + 1;
    const year = res.getFullYear();
    return `${month < 10 ? `0${month}` : `${month}`}/${
      date < 10 ? `0${date}` : `${date}`
    }/${year}`;
  };

  const getExpiresDate = (createdDate) => {
    const res = new Date(createdDate);
    res.setDate(res.getDate() + 10);
    const currentDate = Moment(new Date(), 'DD-MM-YYYY');
    const expiryDate = Moment(res, 'DD-MM-YYYY');
    return expiryDate.diff(currentDate, 'days');
  };

  const getFormatedDate = (payload) => {
    const result = [];
    if (payload && payload.length) {
      payload.forEach((element) => {
        const startDate = element.createdDt
          ? formatstartDate(element.createdDt)
          : null;
        const expiryDate = getExpirationDate(startDate);
        const expiryOn = getExpiresDate(startDate);
        result.push({
          name: element.w9Details?.name,
          billingTin: element.billingTin,
          startDate,
          attestationDetails: element.attestationDetails,
          address: element.address,
          createdDt: element.createdDt,
          caseStatus: element.caseStatus,
          emailID: element.emailID,
          ipAddress: element.ipAddress,
          optumId: element.optumId,
          practiceInformation: element.practiceInformation,
          requestId: element.requestId,
          trackingNumber: element.trackingNumber,
          uuid: element.uuid,
          w9Details: element.w9Details,
          expiryDate,
          expiryOn,
        });
      });
    }
    return result;
  };

  useEffect(() => {
    // Fetch saved drafts
    if (!isSuperUser) {
      retrieveDraft(
        {},
        (res) => {
          const formatted = getFormatedDate(res);
          setTinDraft(formatted);
        },
        (err) => {
          if (err?.faultCode === 'EPGSNF') {
          } else {
            setDraftError(true);
          }
        }
      );
    }

    // Fetch all submitted registrations
    searchRegistrationStatus(
      {
        isSuperUser: isSuperUserFeature,
        isAllSelected: true,
        billingTin: null,
        requestId: null,
        trackingNumber: null,
      },
      (res) => {
        setSubmittedList(Array.isArray(res) ? res : []);
      },
      (err) => {
        if (err?.faultCode === 'EPRSNF') {
        } else {
          setSubmittedError(true);
        }
      }
    );
  }, []);

  return (
    <Wrapper id="view-submissions">
      <V2Card>
        <SectionTitle id="view-submissions-title">
          View submissions
        </SectionTitle>
        <SectionText id="view-submissions-desc-1">
          Review and pick up where you left off on saved registrations or check
          the status of submissions. Please note that claims can be processed
          after TIN approval.
        </SectionText>
        <TabsWrapper>
          <V2Tabs title="View Submissions">
            <V2Tabs.Tab
              label="Submitted"
              rightAddOn={
                <Counter id="submitted-count">{submittedList.length}</Counter>
              }
              subLabel="If you have submitted your TIN registration, a claim—or both—to UnitedHealthcare, you can check on the progress of your submissions here."
            >
              <Loading isLoading={searchRegistrationStatusSaga.isLoading}>
                {!submittedError ? (
                  <SubmittedRegistrations submittedList={submittedList} />
                ) : (
                  <V2Alert
                    status="error"
                    title="We're having trouble loading your submissions right now"
                    dismissible={false}
                    inline
                  >
                    Please refresh the page or try again later.
                  </V2Alert>
                )}
              </Loading>
            </V2Tabs.Tab>
            <V2Tabs.Tab
              label="Saved"
              rightAddOn={<Counter id="saved-count">{tinDraft.length}</Counter>}
              subLabel="These TIN registrations are currently in progress and have yet to be submitted."
            >
              <Loading
                isLoading={isSuperUser ? false : retrieveDraftSaga.isLoading}
              >
                {!draftError ? (
                  <SavedRegistrations
                    tinDraftResult={tinDraft}
                    setTinDraft={setTinDraft}
                    getFormatedDate={getFormatedDate}
                    onYes={onYes}
                  />
                ) : (
                  <V2Alert
                    status="error"
                    title="We're having trouble loading your drafts right now"
                    dismissible={false}
                    inline
                  >
                    Please refresh the page or try again later.
                  </V2Alert>
                )}
              </Loading>
            </V2Tabs.Tab>
          </V2Tabs>
        </TabsWrapper>
      </V2Card>
    </Wrapper>
  );
};
